import { useSectionAccess } from "../../auth/useSectionAccess";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { tmApi, type TmDependencyEdge, type TmTaskStatus, type TmTaskView } from "../../api/tm";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";

/**
 * Dependency section for the task detail page. Two lists:
 * • "Blocked by" — predecessors that must finish before this task can be
 * marked DONE. Rows show a red dot when the predecessor isn't yet done.
 * • "Blocks" — successors that this task is blocking.
 *
 * The picker only writes "blocked by" edges (the picked task becomes a
 * predecessor of the current one) — that's the natural mental model when
 * standing on the detail page of the gated task.
 */
export function TmDependenciesPanel({ taskId }: { taskId: string }) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const toast = useToast();
  const { t } = useTranslation();
  const [blockedBy, setBlockedBy] = useState<TmDependencyEdge[]>([]);
  const [blocks, setBlocks] = useState<TmDependencyEdge[]>([]);
  const [candidates, setCandidates] = useState<TmTaskView[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [b1, b2] = await Promise.all([tmApi.blockedBy(taskId), tmApi.blocks(taskId)]);
      setBlockedBy(b1);
      setBlocks(b2);
    } catch {
      setBlockedBy([]); setBlocks([]);
    }
  }, [taskId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    // Pulling the "mine" list is enough for picking predecessors most of the
    // time. If we ever need a real cross-user search we'd swap for a search
    // endpoint; for now the typeahead filters this list.
    void tmApi.mine().then(setCandidates).catch(() => setCandidates([]));
  }, []);

  const filteredCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const linkedIds = new Set([taskId, ...blockedBy.map((e) => e.otherTaskId)]);
    return candidates
      .filter((t) => !linkedIds.has(t.id))
      .filter((t) => !q || t.title.toLowerCase().includes(q))
      .slice(0, 8);
  }, [candidates, query, blockedBy, taskId]);

  async function add(predecessor: TmTaskView) {
    if (!canWrite) return;
    setBusy(true);
    try {
      await tmApi.addBlockedBy(taskId, predecessor.id);
      setQuery("");
      setOpen(false);
      await refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(edgeId: string) {
    if (!canWrite) return;
    setBusy(true);
    try {
      await tmApi.removeDependency(taskId, edgeId);
      await refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div>
        <div style={subLabel}>{t("tm.deps.blockedBy", "Blocked by")}</div>
        {blockedBy.length === 0 ? (
          <div style={{ color: "var(--ink-4)", fontSize: 12 }}>—</div>
        ) : (
          <ul style={list}>
            {blockedBy.map((edge) => (
              <li key={edge.id} style={row}>
                <span style={dotForStatus(edge.otherTaskStatus)} title={edge.otherTaskStatus} />
                <Link to={`/tm/tasks/${edge.otherTaskId}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {edge.otherTaskTitle}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{t(`tm.status.${edge.otherTaskStatus}`, { defaultValue: humanStatus(edge.otherTaskStatus) })}</div>
                </Link>
                <button
                  type="button"
                  onClick={() => void remove(edge.id)}
                  disabled={!canWrite || busy}
                  aria-label={t("tm.deps.remove", "Remove dependency")}
                  style={removeBtn}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div style={{ position: "relative", marginTop: 6 }}>
          <input
            className="input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            placeholder={t("tm.deps.addBlocker", "Add a blocker — search your tasks…")}
            style={{ height: 30, fontSize: 12.5, width: "100%", padding: "0 10px" }}
            disabled={!canWrite || busy}
          />
          {open && filteredCandidates.length > 0 && (
            <div className="dd-scroll" style={suggestPanel}>
              {filteredCandidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void add(c)}
                  style={suggestRow}
                >
                  <span style={{ fontSize: 12.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.title}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{t(`tm.status.${c.status}`, { defaultValue: humanStatus(c.status) })}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {blocks.length > 0 && (
        <div>
          <div style={subLabel}>{t("tm.deps.blocks", "Blocks")}</div>
          <ul style={list}>
            {blocks.map((edge) => (
              <li key={edge.id} style={row}>
                <span style={dotForStatus(edge.otherTaskStatus)} title={edge.otherTaskStatus} />
                <Link to={`/tm/tasks/${edge.otherTaskId}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {edge.otherTaskTitle}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{t(`tm.status.${edge.otherTaskStatus}`, { defaultValue: humanStatus(edge.otherTaskStatus) })}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function dotForStatus(status: TmTaskStatus): React.CSSProperties {
  // Anything not DONE flags as a real blocker (red); DONE shows green and
  // visually signals "this dependency is cleared".
  const palette = status === "DONE"
    ? { bg: "var(--ok)", ring: "var(--ok-bg)" }
    : status === "CANCELLED"
      ? { bg: "var(--ink-3)", ring: "var(--border)" }
      : { bg: "var(--err)", ring: "var(--err-bg)" };
  return {
    width: 8, height: 8, borderRadius: 999,
    background: palette.bg, boxShadow: `0 0 0 3px ${palette.ring}`,
    flexShrink: 0,
  };
}

function humanStatus(s: TmTaskStatus): string {
  switch (s) {
    case "TODO":        return "To do";
    case "IN_PROGRESS": return "In progress";
    case "IN_REVIEW":   return "In review";
    case "BLOCKED":     return "Blocked";
    case "DONE":        return "Done";
    case "CANCELLED":   return "Cancelled";
  }
}

const subLabel: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, color: "var(--ink-3)",
  letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 6,
};
const list: React.CSSProperties = {
  listStyle: "none", padding: 0, margin: 0,
  display: "flex", flexDirection: "column", gap: 4,
};
const row: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "6px 10px", background: "var(--surface-2)",
  border: "1px solid var(--border)", borderRadius: 6,
};
const removeBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--ink-3)",
  cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0,
};
const suggestPanel: React.CSSProperties = {
  position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: 6, padding: 4, boxShadow: "var(--shadow-2)",
  zIndex: 30, maxHeight: 240, overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 2,
};
const suggestRow: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  padding: "6px 8px", background: "transparent", border: "none",
  cursor: "pointer", textAlign: "left", borderRadius: 4,
};
